import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { PerspectiveCamera, OrbitControls, Text, Box, Sphere, Cylinder, Torus, Cone } from '@react-three/drei';
import * as THREE from 'three';

function ElegantTable() {
  const tableGroupRef = useRef<THREE.Group>(null);
  
  useFrame((state) => {
    if (tableGroupRef.current) {
      // Çok yumuşak breathing animasyonu
      const breathe = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.01;
      tableGroupRef.current.scale.set(1 + breathe, 1, 1 + breathe);
      
      // Çok hafif sallanma
      tableGroupRef.current.rotation.y = Math.sin(state.clock.getElapsedTime() * 0.15) * 0.02;
    }
  });

  return (
    <group ref={tableGroupRef} position={[0, -1, 0]}>
      {/* Şık masa tablası - mermer efekti */}
      <Cylinder args={[2, 2, 0.08, 64]} position={[0, 1, 0]}>
        <meshStandardMaterial 
          color="#F8F6F3" 
          roughness={0.1} 
          metalness={0.05}
          transparent
          opacity={0.95}
        />
      </Cylinder>
      
      {/* Masa kenarı - altın detay */}
      <Torus args={[2, 0.02, 8, 64]} position={[0, 1.04, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial 
          color="#D4AF37" 
          roughness={0.2} 
          metalness={0.8}
        />
      </Torus>
      
      {/* Modern masa ayağı */}
      <Cylinder args={[0.04, 0.08, 2, 16]} position={[0, 0, 0]}>
        <meshStandardMaterial color="#2C2C2C" roughness={0.3} metalness={0.7} />
      </Cylinder>
      
      {/* Masa taban detayı */}
      <Cylinder args={[0.4, 0.4, 0.06, 32]} position={[0, -1, 0]}>
        <meshStandardMaterial color="#1A1A1A" roughness={0.4} metalness={0.6} />
      </Cylinder>
      
      {/* Premium kahve fincanları */}
      <group position={[0.6, 1.1, 0.6]}>
        <Cylinder args={[0.08, 0.07, 0.1, 24]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#1A1A1A" roughness={0.3} metalness={0.2} />
        </Cylinder>
        <Cylinder args={[0.1, 0.1, 0.02, 24]} position={[0, -0.06, 0]}>
          <meshStandardMaterial color="#F5F2ED" roughness={0.2} metalness={0.0} />
        </Cylinder>
        {/* Fincan sapı */}
        <Torus args={[0.04, 0.008, 8, 16]} position={[0.08, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <meshStandardMaterial color="#1A1A1A" roughness={0.3} metalness={0.2} />
        </Torus>
      </group>
      
      <group position={[-0.6, 1.1, 0.6]}>
        <Cylinder args={[0.08, 0.07, 0.1, 24]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#1A1A1A" roughness={0.3} metalness={0.2} />
        </Cylinder>
        <Cylinder args={[0.1, 0.1, 0.02, 24]} position={[0, -0.06, 0]}>
          <meshStandardMaterial color="#F5F2ED" roughness={0.2} metalness={0.0} />
        </Cylinder>
        <Torus args={[0.04, 0.008, 8, 16]} position={[-0.08, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
          <meshStandardMaterial color="#1A1A1A" roughness={0.3} metalness={0.2} />
        </Torus>
      </group>
      
      {/* Premium tabaklar */}
      <Cylinder args={[0.2, 0.2, 0.02, 32]} position={[0.6, 1.05, 0.2]}>
        <meshStandardMaterial color="#F8F6F3" roughness={0.1} metalness={0.0} />
      </Cylinder>
      
      <Cylinder args={[0.2, 0.2, 0.02, 32]} position={[-0.6, 1.05, 0.2]}>
        <meshStandardMaterial color="#F8F6F3" roughness={0.1} metalness={0.0} />
      </Cylinder>
      
      {/* Şık çiçek aranjmanı */}
      <group position={[0, 1.15, 0]}>
        <Cylinder args={[0.12, 0.08, 0.2, 16]} position={[0, 0, 0]}>
          <meshStandardMaterial 
            color="#C0C0C0" 
            roughness={0.1} 
            metalness={0.9}
          />
        </Cylinder>
        
        {/* Çiçekler */}
        <Sphere args={[0.03]} position={[0, 0.15, 0]}>
          <meshStandardMaterial color="#FF6B9D" roughness={0.5} metalness={0.0} />
        </Sphere>
        <Sphere args={[0.025]} position={[0.04, 0.18, 0.02]}>
          <meshStandardMaterial color="#4ECDC4" roughness={0.5} metalness={0.0} />
        </Sphere>
        <Sphere args={[0.025]} position={[-0.03, 0.17, -0.03]}>
          <meshStandardMaterial color="#45B7D1" roughness={0.5} metalness={0.0} />
        </Sphere>
        <Sphere args={[0.02]} position={[0.02, 0.16, -0.04]}>
          <meshStandardMaterial color="#96CEB4" roughness={0.5} metalness={0.0} />
        </Sphere>
      </group>
      
      {/* Premium menü */}
      <Box args={[0.3, 0.4, 0.02]} position={[1, 1.06, -0.5]} rotation={[0, -0.3, 0]}>
        <meshStandardMaterial 
          color="#2C2C2C" 
          roughness={0.1} 
          metalness={0.1}
        />
      </Box>
      
      {/* Altın çatal bıçak takımları */}
      <group position={[0.5, 1.06, 0.1]}>
        <Box args={[0.02, 0.2, 0.01]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#D4AF37" roughness={0.2} metalness={0.8} />
        </Box>
        <Box args={[0.02, 0.2, 0.01]} position={[0.15, 0, 0]}>
          <meshStandardMaterial color="#D4AF37" roughness={0.2} metalness={0.8} />
        </Box>
      </group>
      
      <group position={[-0.65, 1.06, 0.1]}>
        <Box args={[0.02, 0.2, 0.01]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#D4AF37" roughness={0.2} metalness={0.8} />
        </Box>
        <Box args={[0.02, 0.2, 0.01]} position={[0.15, 0, 0]}>
          <meshStandardMaterial color="#D4AF37" roughness={0.2} metalness={0.8} />
        </Box>
      </group>
      
      {/* Şık mum */}
      <group position={[0, 1.15, -0.8]}>
        <Cylinder args={[0.03, 0.03, 0.25, 16]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#F5E6D3" roughness={0.8} metalness={0.0} />
        </Cylinder>
        <Cone args={[0.01, 0.02, 8]} position={[0, 0.14, 0]}>
          <meshStandardMaterial color="#FF4500" roughness={0.9} metalness={0.0} />
        </Cone>
      </group>
    </group>
  );
}

function AmbientDecor() {
  const decorRef = useRef<THREE.Group>(null);
  
  useFrame((state) => {
    if (decorRef.current) {
      decorRef.current.children.forEach((child, index) => {
        child.position.y += Math.sin(state.clock.getElapsedTime() * 0.4 + index * 2) * 0.001;
        child.rotation.y += 0.002 * (index % 2 === 0 ? 1 : -1);
      });
    }
  });

  return (
    <group ref={decorRef}>
      {/* Floating elements - güvenli Unicode karakterler */}
      <Text
        position={[-3, 2, -2]}
        rotation={[0, 0.2, 0]}
        fontSize={0.5}
        color="#D4AF37"
        anchorX="center"
        anchorY="middle"
      >
        ★
      </Text>
      
      <Text
        position={[3, 1.5, -1.5]}
        rotation={[0, -0.2, 0]}
        fontSize={0.4}
        color="#8B7355"
        anchorX="center"
        anchorY="middle"
      >
        ♨
      </Text>
      
      <Text
        position={[-2.5, 0.5, 2]}
        rotation={[0, 0.3, 0]}
        fontSize={0.3}
        color="#A67C52"
        anchorX="center"
        anchorY="middle"
      >
        ♦
      </Text>
      
      <Text
        position={[2.8, 2.2, 1]}
        rotation={[0, -0.3, 0]}
        fontSize={0.35}
        color="#6B5B73"
        anchorX="center"
        anchorY="middle"
      >
        ❀
      </Text>
      
      <Text
        position={[0, 2.5, 0]}
        rotation={[0, 0, 0]}
        fontSize={0.32}
        color="#D4AF37"
        anchorX="center"
        anchorY="middle"
      >
        ◆
      </Text>
      
      <Text
        position={[-2.8, 1.2, 0.5]}
        rotation={[0, 0.6, 0]}
        fontSize={0.33}
        color="#8B7355"
        anchorX="center"
        anchorY="middle"
      >
        ▲
      </Text>
    </group>
  );
}

function SoftParticles() {
  const particlesRef = useRef<THREE.Points>(null);
  
  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.0005;
      particlesRef.current.rotation.x += 0.0002;
    }
  });
  
  const particleCount = 15;
  const positions = new Float32Array(particleCount * 3);
  
  for (let i = 0; i < particleCount; i++) {
    const i3 = i * 3;
    const angle = (i / particleCount) * Math.PI * 2;
    const radius = 4 + Math.random() * 1;
    positions[i3] = Math.cos(angle) * radius;
    positions[i3 + 1] = (Math.random() - 0.5) * 3;
    positions[i3 + 2] = Math.sin(angle) * radius;
  }
  
  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.12}
        color="#D4AF37"
        transparent
        opacity={0.6}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

const RestaurantScene: React.FC = () => {
  return (
    <Canvas style={{ height: '400px', width: '100%', background: 'linear-gradient(180deg, #F5F2ED 0%, #E8E4DC 100%)' }}>
      <PerspectiveCamera makeDefault position={[4, 2, 4]} fov={50} />
      <OrbitControls 
        enablePan={false} 
        enableZoom={false} 
        maxPolarAngle={Math.PI / 2.2}
        minPolarAngle={Math.PI / 4}
        autoRotate
        autoRotateSpeed={0.3}
      />
      
      {/* Premium lighting setup */}
      <ambientLight intensity={0.6} color="#F5F2ED" />
      <directionalLight 
        position={[5, 8, 2]} 
        intensity={1.2} 
        color="#FFF8F0"
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <pointLight position={[-3, 4, -1]} intensity={0.4} color="#D4AF37" />
      <pointLight position={[3, 3, 1]} intensity={0.3} color="#8B7355" />
      
      <ElegantTable />
      <AmbientDecor />
      <SoftParticles />
    </Canvas>
  );
};

export default RestaurantScene;
